import { Box, Grid, GridItem, useToken } from '@chakra-ui/react'
import { ReactNode } from 'react'
import SimpleBar from 'simplebar-react'

import { ColorMode } from '../../theme/ColorMode'
import { CookieDialog } from '../CookieDialog/CookieDialog'
import { SetDocumentTitle } from '../DocumentTitle/SetDocumentTitle'
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary'
import { WidgetError } from '../Errors/WidgetError'
import { SecondarySidebar } from '../SecondarySidebar/SecondarySidebar'
import { Topbar } from '../Topbar/Topbar'

export interface PageLayoutProps {
  aboveContentSlot?: ReactNode
  headerSlot?: ReactNode
  sidebarSlot?: ReactNode
  children?: ReactNode
  darkMode?: boolean
  noPaddingTop?: boolean
  /** When set, updates the browser tab title (store appends ` - Helo`). */
  documentTitle?: string
}

export const PageLayout = ({
  aboveContentSlot,
  headerSlot,
  sidebarSlot,
  darkMode = false,
  children,
  noPaddingTop,
  documentTitle,
  ...props
}: PageLayoutProps) => {
  const [gradientStart, gradientEnd] = useToken('colors', [
    'page.dark.gradientStart',
    'page.dark.gradientEnd',
  ])

  const darkPageBackground =
    darkMode &&
    `linear-gradient(180deg, ${gradientStart} 0.04%, ${gradientEnd} 99.96%)`

  return (
    <ColorMode colorMode={darkMode ? 'dark' : 'light'}>
      {documentTitle ? <SetDocumentTitle title={documentTitle} /> : null}
      <Grid
        gridTemplateColumns="auto 1fr"
        gridTemplateRows="100%"
        zIndex="1"
        position="relative"
      >
        <GridItem>
          {sidebarSlot ? (
            <SecondarySidebar zIndex="4">{sidebarSlot}</SecondarySidebar>
          ) : null}
        </GridItem>
        <Grid gridTemplateColumns="1fr" gridTemplateRows="auto 1fr" zIndex="3">
          {/* Topbar is intentionally NOT wrapped in an error boundary so it
              always renders (including notifications). The header slot has its
              own boundary inside Topbar. */}
          <Topbar headerSlot={headerSlot} zIndex="2" />
          <Box
            flex="1"
            display="flex"
            flexDirection="column"
            as="main"
            overflowY="scroll"
            zIndex="1"
            {...props}
            {...(darkPageBackground
              ? { backgroundImage: darkPageBackground }
              : { backgroundColor: 'white' })}
          >
            <SimpleBar
              style={{
                height: '100%',
                width: '100%',
              }}
            >
              {/* Guard main content so a throwing body can't take down the
                  layout/header. */}
              <ErrorBoundary renderError={() => <WidgetError />}>
                {aboveContentSlot && <Box>{aboveContentSlot}</Box>}
                <Box
                  display="flex"
                  flexDirection="column"
                  height="full"
                  width="full"
                  marginX="auto"
                  paddingBottom="28px"
                  paddingX="28px"
                  paddingTop={noPaddingTop ? '0' : '16px'}
                >
                  {children}
                </Box>
              </ErrorBoundary>
            </SimpleBar>
          </Box>
        </Grid>
        <CookieDialog />
      </Grid>
    </ColorMode>
  )
}
